import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import LanguageSelector from "../components/LanguageSelector";
import { api } from "../App";
import { formatMoney } from "../utils/formatCurrency";
import { getTranslation } from "../i18n/translations";
import {
  FileText,
  CheckCircle2,
  TrendingUp,
  Wallet,
  BarChart3,
  Users,
  Layers,
  EyeOff,
} from "lucide-react";

const monthLabel = (key, t) => {
  const months = t('monthsShort');
  const [y, m] = key.split("-");
  return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

const STATUS_META = {
  draft: { labelKey: "draft", color: "bg-slate-400" },
  pending: { labelKey: "pending", color: "bg-amber-400" },
  sent: { labelKey: "sent", color: "bg-blue-500" },
  approved: { labelKey: "approved", color: "bg-emerald-500" },
  rejected: { labelKey: "rejected", color: "bg-rose-500" },
};

export default function ReportsPage({ user, lang = "tr", setLang }) {
  const t = (key) => getTranslation(lang, key);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/reports/summary")
      .then((res) => setData(res.data))
      .catch(() => toast.error(getTranslation(lang, 'reportsLoadFailed')))
      .finally(() => setLoading(false));
  }, []);

  const money = (v) => formatMoney(v || 0, lang);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100">
        <Sidebar user={user} lang={lang} />
        <div className="lg:ml-64 flex items-center justify-center py-32">
          <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const maxRevenue = Math.max(...data.monthly.map((m) => m.revenue), 1);
  const maxCat = Math.max(...(data.top_categories.map((c) => c.count) || [1]), 1);

  return (
    <div className="min-h-screen bg-slate-100">
      <Sidebar user={user} lang={lang} />
      <div className="lg:ml-64">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-amber-500" />
                {t('reportsTitle')}
              </h1>
              <p className="text-slate-500 text-xs sm:text-sm">{t('reportsSubtitle')}</p>
            </div>
            <LanguageSelector currentLang={lang} onLanguageChange={setLang} />
          </div>
        </header>

        <main className="p-4 sm:p-6 space-y-6" data-testid="reports-page">
          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-500">{t('totalProposals')}</p>
                    <p className="text-2xl font-bold text-slate-900" data-testid="report-total-proposals">
                      {data.total_proposals}
                    </p>
                  </div>
                  <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-500">{t('approvalRate')}</p>
                    <p className="text-2xl font-bold text-emerald-600" data-testid="report-approval-rate">
                      %{data.approval_rate}
                    </p>
                  </div>
                  <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-500">{t('approvedRevenue')}</p>
                    <p className="text-xl sm:text-2xl font-bold text-slate-900" data-testid="report-approved-revenue">
                      {money(data.approved_revenue)}
                    </p>
                    <p className="text-[11px] text-slate-400">{t('pendingColon')} {money(data.pending_revenue)}</p>
                  </div>
                  <div className="w-11 h-11 rounded-full bg-purple-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-amber-700 flex items-center gap-1">
                      {t('estimatedProfit')} <EyeOff className="w-3 h-3" />
                    </p>
                    <p className="text-xl sm:text-2xl font-bold text-amber-700" data-testid="report-profit">
                      {money(data.profit_total)}
                    </p>
                    <p className="text-[11px] text-amber-600/70">{t('profitVisibilityNote')}</p>
                  </div>
                  <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Monthly chart */}
            <Card className="bg-white border-slate-200 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">{t('monthlyChartTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-1.5 h-44" data-testid="monthly-chart">
                  {data.monthly.map((m) => (
                    <div key={m.month} className="flex-1 h-full flex flex-col items-center justify-end gap-1 group relative">
                      <div className="absolute -top-14 hidden group-hover:block bg-slate-900 text-white text-[10px] rounded-md px-2 py-1 whitespace-nowrap z-10">
                        {m.count} teklif<br />{money(m.revenue)}
                      </div>
                      <span className="text-[10px] text-slate-500 font-medium">
                        {m.count > 0 ? m.count : ""}
                      </span>
                      <div
                        className="w-full rounded-t-md bg-gradient-to-t from-amber-500 to-amber-300 transition-all hover:from-amber-600"
                        style={{ height: `${Math.max(Math.sqrt(m.revenue / maxRevenue) * 80, m.count > 0 ? 5 : 1)}%` }}
                      />
                      <span className="text-[9px] text-slate-400 -rotate-45 sm:rotate-0 whitespace-nowrap">
                        {monthLabel(m.month, t)}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Status distribution */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">{t('statusDistribution')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3" data-testid="status-distribution">
                {Object.entries(STATUS_META).map(([key, meta]) => {
                  const count = data.status_counts[key] || 0;
                  const pct = data.total_proposals ? (count / data.total_proposals) * 100 : 0;
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">{t(meta.labelKey)}</span>
                        <span className="font-semibold text-slate-900">{count}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${meta.color}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Top categories */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Layers className="w-5 h-5 text-teal-500" />
                  {t('topCategoriesTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3" data-testid="top-categories">
                {data.top_categories.length === 0 ? (
                  <p className="text-slate-400 text-sm py-4 text-center">{t('noCategoryData')}</p>
                ) : (
                  data.top_categories.map((c) => (
                    <div key={c.category}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-700 truncate">{c.category}</span>
                        <span className="font-semibold text-slate-900">{c.count} {t('itemsWord')}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-teal-400 to-teal-600"
                          style={{ width: `${(c.count / maxCat) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Top customers */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  {t('topCustomersTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent data-testid="top-customers">
                {data.top_customers.length === 0 ? (
                  <p className="text-slate-400 text-sm py-4 text-center">{t('noCustomerData')}</p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {data.top_customers.map((c, i) => (
                      <div key={`${c.name}-${i}`} className="flex items-center justify-between py-2.5">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-xs font-bold flex items-center justify-center flex-shrink-0">
                            {i + 1}
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{c.name}</p>
                            <p className="text-xs text-slate-400">{c.count} {t('proposalWord')}</p>
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-emerald-600 flex-shrink-0">
                          {money(c.revenue)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
}
